import * as React from "react";
import "./Alert.scss";

const AlertContext = React.createContext({
  handleClose: () => {},
});

const Alert = React.forwardRef(
  (
    {
      alertKey,
      placement = "top-right",
      showOverlay = false,
      className = "",
      children,
      ...props
    },
    ref
  ) => {
    const [visible, setVisible] = React.useState(false);

    React.useEffect(() => {
      if (!alertKey) return;
      const dismissed = sessionStorage.getItem(`alert-dismissed-${alertKey}`);
      setVisible(!dismissed);
    }, [alertKey]);

    const handleClose = React.useCallback(() => {
      if (alertKey) {
        sessionStorage.setItem(`alert-dismissed-${alertKey}`, "true");
      }
      setVisible(false);
    }, [alertKey]);

    if (!visible) return null;

    const hasClose = React.Children.toArray(children).some(
      (child) => React.isValidElement(child) && child.type && child.type.displayName === "AlertClose"
    );

    return (
      <AlertContext.Provider value={{ handleClose }}>
        {showOverlay && (
          <div
            className="custom-alert-overlay"
            aria-hidden="true"
          />
        )}
        <div
          ref={ref}
          role="alert"
          className={`custom-alert ${placement} ${className}`.trim()}
          {...props}
        >
          {children}
          {!hasClose && <AlertClose />}
        </div>
      </AlertContext.Provider>
    );
  }
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef(({ className = "", ...props }, ref) => (
  <h2
    ref={ref}
    className={`custom-alert-heading ${className}`.trim()}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef(
  ({ className = "", ...props }, ref) => (
    <p
      ref={ref}
      className={`custom-alert-description ${className}`.trim()}
      {...props}
    />
  )
);
AlertDescription.displayName = "AlertDescription";

const AlertContent = React.forwardRef(({ className = "", ...props }, ref) => (
  <div
    ref={ref}
    className={`custom-alert-content ${className}`.trim()}
    {...props}
  />
));
AlertContent.displayName = "AlertContent";

const AlertClose = React.forwardRef(
  ({ className = "", children = "\u00D7", onClick, ...props }, ref) => {
    const { handleClose } = React.useContext(AlertContext);

    return (
      <button
        ref={ref}
        type="button"
        aria-label="Close alert"
        className={`custom-alert-close ${className}`.trim()}
        onClick={(e) => {
          handleClose();
          onClick?.(e); // allow overriding/hooking into onClick
        }}
        {...props}
      >
        {children}
      </button>
    );
  }
);
AlertClose.displayName = "AlertClose";

export { Alert, AlertTitle, AlertDescription, AlertContent, AlertClose };